/**
 * MetaConfigPopover — floating panel that appears when clicking on the
 * ⚙ Config button of a ::meta block in the CodeMirror editor.
 *
 * For ::meta[module]: form to edit `exports` array and metadata.
 * For ::meta[config]: visual storage mode selector plus raw JSON fallback.
 */

import { OutputSerializer, type ParsedDocument } from "@magic-md/core";
import { useT } from "@magic-md/i18n/react";
import {
    Camera,
    Check,
    HardDriveDownload,
    Settings,
    X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDraggablePopover } from "../hooks/useDraggablePopover";
import { cn } from "../lib/utils";
import { useEditorStore } from "../store/editor-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";
import { ConfigEditorModeButton, DirtyStateBanner, RawConfigEditor } from "./shared";

const outputSerializer = new OutputSerializer();

export interface MetaPopoverTarget {
    domain: string;
    x: number;
    y: number;
    tabId: string;
    paneId?: string;
}

export function MetaConfigPopover({
    target,
    onClose,
}: {
    target: MetaPopoverTarget | null;
    onClose: () => void;
}) {
    const t = useT("editor");
    const ref = useRef<HTMLDivElement>(null);
    const { isDragging, onHeaderMouseDown, position } = useDraggablePopover({
        anchor: target ? { x: target.x, y: target.y } : null,
        width: 420,
        height: 400,
    });

    // Read current meta config from the clicked pane's tab, not the global active tab.
    const targetTabId = target?.tabId ?? null;
    const targetTab = useEditorStore((s) => {
        const id = targetTabId ?? s.activeTabId;
        return s.tabs.find((t) => t.id === id);
    });
    const parsedDocument = useEditorStore((s) => {
        const id = targetTabId ?? s.activeTabId;
        return s.parsedDocumentMap.get(id) ?? (id === s.activeTabId ? s.parsedDocument : null);
    });
    const persistedConfig = useMemo<Record<string, unknown>>(() => {
        if (!target) return {};
        if (target.domain === "module") {
            return (parsedDocument?.meta?.module as unknown as Record<string, unknown>) ?? {};
        }
        if (target.domain === "config") {
            return (parsedDocument?.meta?.config as Record<string, unknown>) ?? {};
        }
        if (target.domain === "output") {
            return (parsedDocument?.meta?.output as unknown as Record<string, unknown>) ?? {};
        }
        if (target.domain === "snapshot") {
            return (parsedDocument?.meta?.snapshot as unknown as Record<string, unknown>) ?? {};
        }
        return {};
    }, [target, parsedDocument]);

    // Draft state
    const [draftConfig, setDraftConfig] = useState<Record<string, unknown>>(
        () => persistedConfig,
    );
    const [isDirty, setIsDirty] = useState(false);
    const [useRawEditor, setUseRawEditor] = useState(false);

    // Reset draft when target changes. Domain alone is insufficient in multi-pane
    // mode because two open documents can both have ::meta[module].
    const targetKey = target ? `${target.tabId}:${target.domain}` : null;
    const lastTargetKey = useRef(targetKey);
    useEffect(() => {
        if (targetKey !== lastTargetKey.current) {
            lastTargetKey.current = targetKey;
            setDraftConfig(persistedConfig);
            setIsDirty(false);
            setUseRawEditor(false);
        }
    }, [targetKey, persistedConfig]);

    // Sync persisted config when not dirty
    useEffect(() => {
        if (!isDirty) {
            setDraftConfig(persistedConfig);
        }
    }, [persistedConfig, isDirty]);

    const updateDraft = useCallback((value: Record<string, unknown>) => {
        setDraftConfig(value);
        setIsDirty(true);
    }, []);

    const applyDraft = useCallback(() => {
        if (!target) return;
        const editorState = useEditorStore.getState();
        const tab = editorState.tabs.find((t) => t.id === target.tabId) ?? targetTab;
        if (!tab) return;

        const updated = outputSerializer.updateMetaConfig(
            tab.content,
            target.domain,
            draftConfig,
        );
        if (updated !== tab.content) {
            editorState.setContentImmediate(updated, tab.id);
        }
        setIsDirty(false);
    }, [target, targetTab, draftConfig]);

    const discardDraft = useCallback(() => {
        setDraftConfig(persistedConfig);
        setIsDirty(false);
    }, [persistedConfig]);

    // Close on outside click
    useEffect(() => {
        if (!target) return;
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as HTMLElement)) {
                onClose();
            }
        };
        const timer = setTimeout(
            () => document.addEventListener("mousedown", handler),
            50,
        );
        return () => {
            clearTimeout(timer);
            document.removeEventListener("mousedown", handler);
        };
    }, [target, onClose]);

    // Close on Escape
    useEffect(() => {
        if (!target) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [target, onClose]);

    if (!target) return null;

    const isModule = target.domain === "module";
    const isConfig = target.domain === "config";
    const isOutput = target.domain === "output";
    const isSnapshot = target.domain === "snapshot";
    const domainTitle = isModule
        ? t("meta.domain.module")
        : isOutput
            ? t("meta.domain.output")
            : isSnapshot
                ? t("meta.domain.snapshot")
                : t("meta.domain.document");

    // Position the popover below the click point, clamped to viewport
    const style: React.CSSProperties = {
        position: "fixed",
        left: position?.x ?? target.x,
        top: position?.y ?? target.y + 4,
        zIndex: 100,
    };

    return (
        <div
            ref={ref}
            style={style}
            className="w-[420px] rounded-xl border border-border bg-background shadow-xl overflow-hidden"
        >
            {/* Header */}
            <div
                className="flex items-center gap-2 px-3 py-2 bg-muted/50 border-b border-border cursor-grab active:cursor-grabbing select-none"
                role="presentation"
                onMouseDownCapture={onHeaderMouseDown}
                title={t("meta.dragTitle")}
            >
                <Settings
                    size={14}
                    className="shrink-0 theme-accent-text"
                    style={getThemeAccentStyle("warning")}
                />
                <span className="text-sm font-semibold truncate">
                    {domainTitle}
                </span>
                <span className="text-[10px] text-muted-foreground px-1.5 py-0.5 bg-muted rounded-full shrink-0">
                    ::meta[{target.domain}]
                </span>
                {isDragging && (
                    <span className="text-[10px] text-muted-foreground/70 shrink-0">
                        {t("meta.moving")}
                    </span>
                )}
                <div className="flex-1" />
                {(isModule || isConfig || isOutput || isSnapshot) && (
                    <ConfigEditorModeButton
                        useRawEditor={useRawEditor}
                        onChange={setUseRawEditor}
                    />
                )}
                <button
                    type="button"
                    className="p-0.5 rounded hover:bg-muted text-muted-foreground"
                    onClick={onClose}
                >
                    <X size={14} />
                </button>
            </div>

            {/* Content */}
            <div className="max-h-[calc(100vh-200px)] overflow-auto">
                <div className="p-3 space-y-2">
                    {isDirty && (
                        <DirtyStateBanner onSave={applyDraft} onDiscard={discardDraft} />
                    )}

                    {isModule ? (
                        <ModuleConfigEditor
                            config={draftConfig}
                            onChange={updateDraft}
                            useRawEditor={useRawEditor}
                            parsedDocument={parsedDocument ?? null}
                        />
                    ) : isOutput ? (
                        useRawEditor ? (
                            <RawConfigEditor config={draftConfig} onChange={updateDraft} />
                        ) : (
                            <StorageModeConfigEditor
                                domain="output"
                                config={draftConfig}
                                onChange={updateDraft}
                            />
                        )
                    ) : isConfig ? (
                        useRawEditor ? (
                            <RawConfigEditor config={draftConfig} onChange={updateDraft} />
                        ) : (
                            <StorageModeConfigEditor
                                domain="config"
                                config={draftConfig}
                                onChange={updateDraft}
                            />
                        )
                    ) : isSnapshot ? (
                        useRawEditor ? (
                            <RawConfigEditor config={draftConfig} onChange={updateDraft} />
                        ) : (
                            <SnapshotConfigEditor config={draftConfig} onChange={updateDraft} />
                        )
                    ) : (
                        <RawConfigEditor config={draftConfig} onChange={updateDraft} />
                    )}
                </div>
            </div>
        </div>
    );
}

/* ── Module-specific visual editor ── */

function getExportLocalName(entry: string): string {
    return entry.split(":")[0]?.trim() ?? entry;
}

function ModuleConfigEditor({
    config,
    onChange,
    useRawEditor,
    parsedDocument,
}: {
    config: Record<string, unknown>;
    onChange: (value: Record<string, unknown>) => void;
    useRawEditor: boolean;
    parsedDocument: ParsedDocument | null;
}) {
    const t = useT("editor");
    // All exportable IDs available in the document (plugin + content + use materializations).
    const allDefinitionIds = useMemo(() => {
        const ids: { id: string; label?: string }[] = [];
        const seen = new Set<string>();
        const add = (id: string, label?: string) => {
            if (seen.has(id)) return;
            seen.add(id);
            ids.push({ id, label });
        };
        if (parsedDocument?.definitions) {
            for (const [id, def] of parsedDocument.definitions.entries()) {
                add(id, def.pluginName);
            }
        }
        if (parsedDocument?.contentDefinitions) {
            for (const [id, contentDef] of parsedDocument.contentDefinitions.entries()) {
                add(id, contentDef.template ? t("meta.symbol.contentTemplate") : t("meta.symbol.content"));
            }
        }
        if (parsedDocument?.uses) {
            for (const use of parsedDocument.uses) {
                add(use.id, `use -> ${use.source}`);
            }
        }
        return ids.sort((a, b) => a.id.localeCompare(b.id));
    }, [parsedDocument, t]);

    const exports = useMemo(() => {
        const arr = (config.exports ?? []) as string[];
        return Array.isArray(arr) ? arr : [];
    }, [config]);

    const exportSet = useMemo(() => new Set(exports.map(getExportLocalName)), [exports]);

    const toggleExport = useCallback(
        (name: string) => {
            const next = exportSet.has(name)
                ? exports.filter((e) => getExportLocalName(e) !== name)
                : [...exports, name];
            onChange({ ...config, exports: next });
        },
        [exports, exportSet, config, onChange],
    );

    if (useRawEditor) {
        return <RawConfigEditor config={config} onChange={onChange} />;
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <span className="text-[10px] font-medium text-muted-foreground">
                    {t("meta.exports")}
                </span>
                <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground/60">
                        {exports.length}/{allDefinitionIds.length}
                    </span>
                </div>
            </div>

            {/* Checkbox list of all exportable symbols */}
            {allDefinitionIds.length > 0 ? (
                <div className="space-y-0.5">
                    {allDefinitionIds.map((id) => {
                        const checked = exportSet.has(id.id);
                        return (
                            <button
                                key={id.id}
                                type="button"
                                className={cn(
                                    "flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-left transition-colors cursor-pointer",
                                    checked
                                        ? "bg-primary/8 border border-primary/20"
                                        : "bg-muted/30 border border-transparent hover:bg-muted/60",
                                )}
                                onClick={() => toggleExport(id.id)}
                            >
                                <span
                                    className={cn(
                                        "flex items-center justify-center w-3.5 h-3.5 rounded border shrink-0 transition-colors",
                                        checked
                                            ? "bg-primary border-primary text-primary-foreground"
                                            : "border-border bg-background",
                                    )}
                                >
                                    {checked && <Check size={9} strokeWidth={3} />}
                                </span>
                                <span className="text-[11px] font-mono truncate flex-1">
                                    {id.id}
                                </span>
                                {id.label && (
                                    <span className="text-[9px] text-muted-foreground/60 shrink-0">
                                        {id.label}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            ) : (
                <div className="text-[10px] text-muted-foreground/60 bg-muted/30 rounded px-2 py-1.5 text-center">
                    {t("meta.noDefinitions")}
                </div>
            )}

            {/* Show exports that exist in config but not in definitions (stale/manual) */}
            {exports.filter((e) => !allDefinitionIds.some((d) => d.id === getExportLocalName(e))).length > 0 && (
                <div className="space-y-0.5">
                    <span className="text-[9px] font-medium text-muted-foreground/50 uppercase tracking-wider">
                        {t("meta.otherExports")}
                    </span>
                    {exports
                        .filter((e) => !allDefinitionIds.some((d) => d.id === getExportLocalName(e)))
                        .map((id) => (
                            <button
                                key={id}
                                type="button"
                                className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md bg-primary/8 border border-primary/20 text-left cursor-pointer"
                                onClick={() => toggleExport(id)}
                            >
                                <span className="flex items-center justify-center w-3.5 h-3.5 rounded border shrink-0 bg-primary border-primary text-primary-foreground">
                                    <Check size={9} strokeWidth={3} />
                                </span>
                                <span className="text-[11px] font-mono truncate flex-1 text-muted-foreground">
                                    {id}
                                </span>
                                <span
                                    className="text-[9px] shrink-0 theme-accent-text"
                                    style={getThemeAccentStyle("warning")}
                                >
                                    {t("common.notFound")}
                                </span>
                            </button>
                        ))}
                </div>
            )}
        </div>
    );
}

/** Visual editor shared by ::meta[config] and ::meta[output]. */
function StorageModeConfigEditor({
    domain,
    config,
    onChange,
}: {
    domain: "config" | "output";
    config: Record<string, unknown>;
    onChange: (value: Record<string, unknown>) => void;
}) {
    const t = useT("editor");
    const mode = (config.mode as string) ?? "inline";

    const setMode = useCallback(
        (m: string) => {
            if (m === "inline") {
                // Remove mode key entirely to mean "default/inline"
                const { mode: _removed, ...rest } = config;
                onChange(Object.keys(rest).length > 0 ? rest : {});
            } else {
                onChange({ ...config, mode: m });
            }
        },
        [config, onChange],
    );

    const options: { value: string; label: string; desc: string }[] = [
        {
            value: "external",
            label: t(domain === "config" ? "meta.config.external" : "meta.output.external"),
            desc: t(
                domain === "config"
                    ? "meta.config.externalDesc"
                    : "meta.output.externalDesc",
            ),
        },
        {
            value: "inline",
            label: t(domain === "config" ? "meta.config.inline" : "meta.output.inline"),
            desc: t(
                domain === "config"
                    ? "meta.config.inlineDesc"
                    : "meta.output.inlineDesc",
            ),
        },
    ];

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <HardDriveDownload size={12} className="text-muted-foreground shrink-0" />
                <span className="text-[10px] font-medium text-muted-foreground">
                    {t(
                        domain === "config"
                            ? "meta.config.storageMode"
                            : "meta.output.storageMode",
                    )}
                </span>
            </div>

            <div className="space-y-1.5">
                {options.map((opt) => {
                    const selected = mode === opt.value || (opt.value === "inline" && mode !== "external");
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            className={cn(
                                "flex flex-col gap-0.5 w-full px-3 py-2 rounded-lg text-left transition-colors cursor-pointer",
                                selected
                                    ? "bg-primary/8 border border-primary/20"
                                    : "bg-muted/30 border border-transparent hover:bg-muted/60",
                            )}
                            onClick={() => setMode(opt.value)}
                        >
                            <span className="text-[11px] font-medium">
                                {opt.label}
                            </span>
                            <span className="text-[10px] text-muted-foreground/60 leading-tight">
                                {opt.desc}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/* ── SnapshotConfigEditor — visual editor for ::meta[snapshot] ── */

type SnapshotMode = "manual" | "on-save" | "off";

function SnapshotConfigEditor({
    config,
    onChange,
}: {
    config: Record<string, unknown>;
    onChange: (value: Record<string, unknown>) => void;
}) {
    const t = useT("editor");
    const enabled = config.enabled !== false; // default true
    const mode = (config.mode as SnapshotMode) ?? "on-save";
    const retention = (config.retention as Record<string, unknown> | undefined) ?? {};
    const keepCount = retention.keepCount as number | undefined;
    const keepDays = retention.keepDays as number | undefined;
    const keepTagged = retention.keepTagged !== false; // default true

    const setField = useCallback(
        (patch: Record<string, unknown>) => {
            const next = { ...config, ...patch };
            // Strip default values to keep markdown clean
            if (next.enabled === true) delete next.enabled;
            if (next.mode === "on-save") delete next.mode;
            delete next.default;
            const ret = next.retention as Record<string, unknown> | undefined;
            if (ret) {
                const cleaned: Record<string, unknown> = {};
                if (typeof ret.keepCount === "number" && ret.keepCount > 0) cleaned.keepCount = ret.keepCount;
                if (typeof ret.keepDays === "number" && ret.keepDays > 0) cleaned.keepDays = ret.keepDays;
                if (ret.keepTagged === false) cleaned.keepTagged = false;
                if (Object.keys(cleaned).length > 0) next.retention = cleaned;
                else delete next.retention;
            }
            onChange(next);
        },
        [config, onChange],
    );

    const setRetention = useCallback(
        (patch: Record<string, unknown>) => {
            setField({ retention: { ...retention, ...patch } });
        },
        [setField, retention],
    );

    const modeOptions: { value: SnapshotMode; label: string; desc: string }[] = [
        { value: "on-save", label: t("meta.snapshot.mode.onSave"), desc: t("meta.snapshot.mode.onSaveDesc") },
        { value: "manual", label: t("meta.snapshot.mode.manual"), desc: t("meta.snapshot.mode.manualDesc") },
        { value: "off", label: t("meta.snapshot.mode.off"), desc: t("meta.snapshot.mode.offDesc") },
    ];

    return (
        <div className="space-y-4">
            {/* Enable toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
                <span
                    className={cn(
                        "flex items-center justify-center w-3.5 h-3.5 rounded border shrink-0 transition-colors",
                        enabled
                            ? "bg-primary border-primary text-primary-foreground"
                            : "border-border bg-background",
                    )}
                >
                    {enabled && <Check size={9} strokeWidth={3} />}
                </span>
                <span className="text-[11px] font-medium">{t("meta.snapshot.enable")}</span>
                <input
                    type="checkbox"
                    className="sr-only"
                    checked={enabled}
                    onChange={(e) => setField({ enabled: e.target.checked })}
                />
            </label>

            {/* Trigger mode */}
            <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                    <Camera size={12} className="text-muted-foreground shrink-0" />
                    <span className="text-[10px] font-medium text-muted-foreground">
                        {t("meta.snapshot.triggerMode")}
                    </span>
                </div>
                <div className="space-y-1">
                    {modeOptions.map((opt) => {
                        const selected = mode === opt.value;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                disabled={!enabled}
                                className={cn(
                                    "flex flex-col gap-0.5 w-full px-3 py-1.5 rounded-lg text-left transition-colors cursor-pointer",
                                    selected
                                        ? "bg-primary/8 border border-primary/20"
                                        : "bg-muted/30 border border-transparent hover:bg-muted/60",
                                    !enabled && "opacity-50 cursor-not-allowed",
                                )}
                                onClick={() => setField({ mode: opt.value })}
                            >
                                <span className="text-[11px] font-medium">{opt.label}</span>
                                <span className="text-[10px] text-muted-foreground/60 leading-tight">
                                    {opt.desc}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Retention */}
            <div className="space-y-2 pt-1 border-t border-border">
                <span className="text-[10px] font-medium text-muted-foreground">
                    {t("meta.snapshot.retention")}
                </span>
                <div className="grid grid-cols-2 gap-2">
                    <label className="flex flex-col gap-1">
                        <span className="text-[10px] text-muted-foreground/70">
                            {t("meta.snapshot.keepCount")}
                        </span>
                        <input
                            type="number"
                            min={0}
                            placeholder={t("common.unlimited")}
                            disabled={!enabled}
                            className="h-7 rounded border border-border bg-background px-2 text-[11px] font-mono"
                            value={keepCount ?? ""}
                            onChange={(e) => {
                                const v = e.target.value;
                                setRetention({ keepCount: v === "" ? undefined : Number(v) });
                            }}
                        />
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="text-[10px] text-muted-foreground/70">{t("meta.snapshot.keepDays")}</span>
                        <input
                            type="number"
                            min={0}
                            placeholder={t("common.unlimited")}
                            disabled={!enabled}
                            className="h-7 rounded border border-border bg-background px-2 text-[11px] font-mono"
                            value={keepDays ?? ""}
                            onChange={(e) => {
                                const v = e.target.value;
                                setRetention({ keepDays: v === "" ? undefined : Number(v) });
                            }}
                        />
                    </label>
                </div>
                <label className="flex items-center gap-2 cursor-pointer mt-1">
                    <span
                        className={cn(
                            "flex items-center justify-center w-3.5 h-3.5 rounded border shrink-0 transition-colors",
                            keepTagged
                                ? "bg-primary border-primary text-primary-foreground"
                                : "border-border bg-background",
                            !enabled && "opacity-50",
                        )}
                    >
                        {keepTagged && <Check size={9} strokeWidth={3} />}
                    </span>
                    <span className={cn("text-[11px]", !enabled && "opacity-50")}>
                        {t("meta.snapshot.keepTagged")}
                    </span>
                    <input
                        type="checkbox"
                        className="sr-only"
                        checked={keepTagged}
                        disabled={!enabled}
                        onChange={(e) => setRetention({ keepTagged: e.target.checked })}
                    />
                </label>
            </div>

            <div className="text-[10px] text-muted-foreground/50 bg-muted/20 rounded px-2 py-1.5 leading-relaxed">
                {t("meta.snapshot.retentionHint")}
            </div>
        </div>
    );
}
